import { Head, Link, router, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type RecordingKind = 'file' | 'spotify' | 'youtube'

interface TuneRef {
  id: number
  name: string
}

interface Recording {
  id: number
  kind: string
  url: string | null
  title: string | null
  primary: boolean
  audio_file: string | null
}

interface SetDetail {
  id: number
  name: string
  tunes: TuneRef[]
  recordings: Recording[]
}

interface Props {
  set: SetDetail
}

const RECORDING_KINDS: RecordingKind[] = ['file', 'spotify', 'youtube']

export default function Show({ set }: Props) {
  const recForm = useForm<{
    kind: RecordingKind
    title: string
    url: string
    audio_file: File | null
  }>({ kind: 'file', title: '', url: '', audio_file: null })

  function addRecording(e: FormEvent) {
    e.preventDefault()
    recForm.post(`/sets/${set.id}/recordings`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => recForm.reset(),
    })
  }

  function makePrimary(recordingId: number) {
    router.patch(
      `/sets/${set.id}/recordings/${recordingId}/set_primary`,
      {},
      { preserveScroll: true },
    )
  }

  function deleteRecording(recordingId: number) {
    if (confirm('Delete this recording?')) {
      router.delete(`/sets/${set.id}/recordings/${recordingId}`, { preserveScroll: true })
    }
  }

  function destroy() {
    if (confirm(`Delete "${set.name}"?`)) {
      router.delete(`/sets/${set.id}`)
    }
  }

  return (
    <>
      <Head title={set.name} />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link href="/sets" className="text-sm text-muted-foreground hover:underline">
            ← Sets
          </Link>
          <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight">{set.name}</h1>
        </div>
        <Button variant="outline" asChild>
          <Link href={`/sets/${set.id}/edit`}>Edit</Link>
        </Button>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Tunes</CardTitle>
          </CardHeader>
          <CardContent>
            {set.tunes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tunes in this set yet.</p>
            ) : (
              <ol className="space-y-2">
                {set.tunes.map((tune, index) => (
                  <li key={tune.id} className="flex items-center gap-2">
                    <span className="w-5 text-sm text-muted-foreground">{index + 1}.</span>
                    <Link
                      href={`/tunes/${tune.id}`}
                      className="font-display font-medium hover:underline"
                    >
                      {tune.name}
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recordings</CardTitle>
          </CardHeader>
          <CardContent>
            {set.recordings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recordings yet.</p>
            ) : (
              <ul className="space-y-3">
                {set.recordings.map((r) => (
                  <li key={r.id} className="rounded-md border p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline">{r.kind}</Badge>
                      {r.title && <span className="font-medium">{r.title}</span>}
                      {r.primary && <Badge variant="secondary">primary</Badge>}
                      <div className="ml-auto flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={r.primary}
                          onClick={() => makePrimary(r.id)}
                        >
                          {r.primary ? 'Primary' : 'Make primary'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteRecording(r.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    {r.audio_file ? (
                      <audio controls src={r.audio_file} className="mt-2 w-full">
                        <track kind="captions" />
                      </audio>
                    ) : (
                      r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-sm text-primary hover:underline"
                        >
                          {r.url}
                        </a>
                      )
                    )}
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={addRecording} className="mt-4 space-y-3 border-t pt-4">
              <p className="text-sm font-medium">Add recording</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="rec-kind">Kind</Label>
                  <Select
                    value={recForm.data.kind}
                    onValueChange={(v) => recForm.setData('kind', v as RecordingKind)}
                  >
                    <SelectTrigger id="rec-kind" className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECORDING_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {k}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {recForm.data.kind === 'file' ? (
                  <div className="space-y-1">
                    <Label htmlFor="rec-file">Audio file</Label>
                    <Input
                      id="rec-file"
                      type="file"
                      accept="audio/*,.m4a,.mp3,.wav,.aac,.aiff,.caf,.m4b,.ogg,.opus,.flac"
                      onChange={(e) => recForm.setData('audio_file', e.target.files?.[0] ?? null)}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label htmlFor="rec-url">URL</Label>
                    <Input
                      id="rec-url"
                      type="url"
                      placeholder="https://…"
                      value={recForm.data.url}
                      onChange={(e) => recForm.setData('url', e.target.value)}
                    />
                  </div>
                )}

                <div className="space-y-1">
                  <Label htmlFor="rec-title">Title (optional)</Label>
                  <Input
                    id="rec-title"
                    value={recForm.data.title}
                    onChange={(e) => recForm.setData('title', e.target.value)}
                  />
                </div>

                <Button type="submit" size="sm" disabled={recForm.processing}>
                  Add
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Button variant="destructive" onClick={destroy}>
          Delete set
        </Button>
      </div>
    </>
  )
}
